/**
 * RESPONSIBILITY: a dropdown that picks any number of values from a long list.
 *
 * A native <select multiple> is a scrolling box that needs Ctrl held to pick a second
 * value — nobody discovers that. Chips are right for six values and wrong for forty
 * series. So: one button saying what is picked ("All series", "Ephesians", "Ephesians +2"),
 * opening a panel of checkboxes (James, 2026-10-17: long lists are a dropdown,
 * multi-select).
 *
 * A <details>, so it opens and closes with no script and is announced as expandable.
 * It closes on a click elsewhere, on Escape (handing focus back to its button, not
 * stranding it on a hidden checkbox) and when focus tabs out of it, because an open
 * panel left over the page is in the way of the very results it changed.
 */
import { useEffect, useRef } from 'react';

export interface FilterPickProps {
  /** Shown when nothing is picked: "All series". Also the button's name to a screen reader. */
  allLabel: string;
  options: readonly string[];
  picked: readonly string[];
  onChange: (next: string[]) => void;
}

export function FilterPick({ allLabel, options, picked, onChange }: FilterPickProps) {
  const box = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const d = box.current;
      if (d?.open && !d.contains(e.target as Node)) d.open = false;
    };
    const onKey = (e: KeyboardEvent) => {
      const d = box.current;
      if (e.key !== 'Escape' || !d?.open) return;
      d.open = false;
      d.querySelector('summary')?.focus();
    };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('click', onClick); document.removeEventListener('keydown', onKey); };
  }, []);

  // A pick whose value is no longer offered would go on filtering, invisibly. Drop it.
  const stale = picked.some((v) => !options.includes(v));
  useEffect(() => {
    if (stale) onChange(picked.filter((v) => options.includes(v)));
  }, [stale, picked, options, onChange]);

  if (options.length === 0) return null;
  const summary = picked.length === 0 ? allLabel
    : picked.length === 1 ? picked[0] : `${picked[0]} +${picked.length - 1}`;
  const toggle = (v: string) =>
    onChange(picked.includes(v) ? picked.filter((x) => x !== v) : [...picked, v]);

  return (
    <details
      className={`pick${picked.length ? ' pick-on' : ''}`} ref={box}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) e.currentTarget.open = false;
      }}
    >
      <summary className="pick-button" aria-label={picked.length ? `${allLabel}: ${summary}` : allLabel}>
        {summary}
      </summary>
      <div className="pick-panel" role="group" aria-label={allLabel}>
        {options.map((v) => (
          <label key={v} className="pick-opt">
            <input type="checkbox" checked={picked.includes(v)} onChange={() => toggle(v)} />
            <span>{v}</span>
          </label>
        ))}
      </div>
    </details>
  );
}
